import { inject, Injectable } from '@angular/core';
import { CanvasContentContributor } from '@panaya/ai-chat/features';
import { CanvasRequestContext } from '@panaya/ai-chat/shared';
import { CanvasEntityMappingRegistry } from './mapping/canvas-entity-mapping.registry';
import { DraftStore } from './draft.store';

const DEFAULT_COUNT_KEY = 'aichat.canvas.draft.card.count';

const SELECTION_REQUIRED_KEY = 'aichat.canvas.draft.selection_required';

/** What the canvas frame asks of its content, answered from the draft, so the frame itself never learns what a draft is. */
@Injectable({ providedIn: 'root' })
export class DraftContentContributor implements CanvasContentContributor {
    private readonly store = inject(DraftStore);

    private readonly mappings = inject(CanvasEntityMappingRegistry);

    /** `draft_id`, `version` and the checked ids for the `canvas` request context; nothing before a draft arrives. */
    public requestContext(): Partial<CanvasRequestContext> {
        const draft = this.store.draft();

        if (!draft) {
            return {};
        }

        return { draft_id: draft.draft_id, version: draft.version, selected_ids: this.store.selectedIds() };
    }

    /** The primary action (Create) needs a ready draft with at least one entity checked. */
    public primaryActionEnabled(): boolean {
        return this.store.isReady() && this.store.selectedCount() > 0;
    }

    /** Why Create is disabled, shown as its tooltip: it needs a ready draft with at least one entity checked. */
    public primaryActionHint(): string | null {
        return this.store.draft() ? SELECTION_REQUIRED_KEY : null;
    }

    public busy(): boolean {
        return this.store.status() === 'filling';
    }

    /** A draft arrived, even an empty or failed one: the display then shows its own empty or error state. */
    public hasContent(): boolean {
        return this.store.draft() !== null;
    }

    public title(): string | null {
        return this.store.draft()?.title ?? null;
    }

    /** A failed revise keeps the last good entities on screen; its message goes under them. */
    public errorMessage(): string | null {
        return this.store.status() === 'error' && this.store.entities().length > 0 ? this.store.message() : null;
    }

    /** The host key for "{{count}} tests" from the draft's entity type mapping, else the library's generic one. */
    public countLabelKey(): string {
        const entityType = this.store.draft()?.entity_type;

        return (entityType && this.mappings.find(entityType)?.countLabelKey) || DEFAULT_COUNT_KEY;
    }
}
